import { useCallback, useEffect, useState } from "react";

/**
 * Manage per-column visibility with localStorage persistence.
 *
 * @param storageKey unique key per modal/feature
 * @param defaultIds column IDs visible on first use (before any user preference saved)
 * @param lockedIds IDs that MUST stay visible (toggle is a no-op)
 * @param allIds every valid column ID; stored IDs outside this list are dropped (defaults to `defaultIds`)
 */
export const useDisplayColumns = <TId extends string>(
  storageKey: string,
  defaultIds: readonly TId[],
  lockedIds: readonly TId[] = [],
  allIds: readonly TId[] = defaultIds,
) => {
  // Read initial state from localStorage (SSR-safe; guards typeof window).
  const [visibleIds, setVisibleIds] = useState<Set<TId>>(() => {
    if (typeof window === "undefined") return new Set(defaultIds);
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return new Set(defaultIds);
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Set(defaultIds);
      // Filter to valid IDs only (in case a stored ID no longer exists).
      const filtered = parsed.filter((x): x is TId => typeof x === "string" && (allIds as readonly string[]).includes(x));
      // Ensure locked IDs are always present.
      const merged = new Set<TId>(filtered);
      lockedIds.forEach((id) => merged.add(id));
      return merged;
    } catch (err) {
      console.error(`[useDisplayColumns] read failed for key "${storageKey}"`, err);
      return new Set(defaultIds);
    }
  });

  // Write-through to localStorage on every change.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(Array.from(visibleIds)));
    } catch (err) {
      console.error(`[useDisplayColumns] write failed for key "${storageKey}"`, err);
    }
  }, [storageKey, visibleIds]);

  const toggle = useCallback(
    (id: TId) => {
      // Locked IDs cannot be hidden.
      if (lockedIds.includes(id)) return;
      setVisibleIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [lockedIds],
  );

  const reset = useCallback(() => {
    setVisibleIds(new Set(defaultIds));
  }, [defaultIds]);

  return { visibleIds, toggle, reset };
};
